import { useTranslation } from 'react-i18next';

import { useSse } from '../../contexts/SseContext';
import { ReceivedCode } from '../../types/api';
import { Loader } from '../ui/Loader';
import { formatDate } from '../../utils/dateFormatter';
import { useLanguage } from '../../hooks/useLanguage';

interface SmsCheckerProps {
  temporaryNumberId: string;
  phoneNumber: string;
  isCodeReceived: boolean;
  receivedCodes?: ReceivedCode[];
  /** When true, only show SSE connection status (no codes list). Use when codes are shown in a separate table. */
  connectionOnly?: boolean;
}

export function SmsChecker({
  phoneNumber,
  isCodeReceived,
  receivedCodes = [],
  connectionOnly = false,
}: SmsCheckerProps) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { getCodeForNumber, connectionState, isConnected } = useSse();

  // Get latest code from SSE if available
  const sseCodeData = isConnected ? getCodeForNumber(phoneNumber) : null;
  const latestSseCode = sseCodeData?.code || null;

  // Combine receivedCodes from API and SSE code
  const allCodes: (ReceivedCode & { isFromSse?: boolean })[] = [...receivedCodes];
  if (latestSseCode && !allCodes.some((c) => c.code === latestSseCode)) {
    allCodes.unshift({
      code: latestSseCode,
      rawMessage: latestSseCode,
      source: 'webhook',
      receivedAt: new Date().toISOString(),
      isFromSse: true,
    });
  }

  // Sort by receivedAt (most recent first)
  const sortedCodes = allCodes.sort((a, b) => {
    return new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime();
  });

  const hasCodes = sortedCodes.length > 0;
  const isWaitingForCode = !isCodeReceived && !hasCodes;

  return (
    <div className="bg-neutral-dark/50 backdrop-blur-lg border border-neutral-dark rounded-lg p-6">
      {/* Connection Status Indicator */}
      {isConnected && (
        <div className="mb-4 flex items-center justify-center gap-2 text-sm">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-green-500 font-body">
            {t('temporaryNumbers.sse.connected')}
          </span>
        </div>
      )}
      {connectionState === 'connecting' && (
        <div className="mb-4 flex items-center justify-center gap-2 text-sm">
          <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
          <span className="text-yellow-500 font-body">
            {t('temporaryNumbers.sse.connecting')}
          </span>
        </div>
      )}
      {connectionState === 'error' && (
        <div className="mb-4 flex items-center justify-center gap-2 text-sm">
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-red-500 font-body">
            {t('temporaryNumbers.sse.error')}
          </span>
        </div>
      )}

      {connectionOnly && (
        <p className="text-sm text-neutral-medium font-body text-center">
          {t('temporaryNumbers.sse.listeningForNew')}
        </p>
      )}

      {/* Waiting for Code - Show Loader */}
      {!connectionOnly && isWaitingForCode && (
        <div className="py-8">
          <Loader
            size="lg"
            text={t('temporaryNumbers.sms.waitingForCode')}
            className="w-full"
          />
        </div>
      )}

      {/* Show Codes List */}
      {!connectionOnly && hasCodes && (
        <div className="space-y-4">
          <h3 className="text-base sm:text-lg font-heading font-semibold text-neutral-light mb-3 sm:mb-4">
            {t('temporaryNumbers.sms.receivedCodes')}
          </h3>
          <div className="space-y-3">
            {sortedCodes.map((code, index) => {
              const showRawMessage = code.rawMessage && code.rawMessage.trim() !== code.code;
              return (
                <div
                  key={`${code.code}-${code.receivedAt}-${index}`}
                  className={`rounded-xl p-4 sm:p-5 ${
                    index === 0 && code.isFromSse
                      ? 'border border-brand-accent/50 bg-brand-accent/10'
                      : 'bg-neutral-dark/50 border border-neutral-dark'
                  }`}
                >
                  {/* Code – single prominent display, no duplicate */}
                  <div className="text-center sm:text-left mb-3">
                    <p className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent tracking-widest tabular-nums break-all">
                      {code.code}
                    </p>
                  </div>
                  {/* Badges – wrap on mobile */}
                  <div className="flex flex-wrap gap-2 mb-2">
                    {index === 0 && (
                      <span className="inline-flex items-center px-2.5 py-1 bg-brand-accent/20 text-brand-accent text-xs font-body rounded-full">
                        {t('temporaryNumbers.sms.latest')}
                      </span>
                    )}
                    {code.isFromSse && (
                      <span className="inline-flex items-center px-2.5 py-1 bg-green-500/20 text-green-500 text-xs font-body rounded-full">
                        {t('temporaryNumbers.sms.realTime')}
                      </span>
                    )}
                  </div>
                  {/* Raw message only if different from code */}
                  {showRawMessage && (
                    <p className="text-sm text-neutral-medium font-body mb-2 break-all">
                      {code.rawMessage}
                    </p>
                  )}
                  {/* Received date – compact on mobile */}
                  <p className="text-xs text-neutral-medium font-body">
                    {t('temporaryNumbers.sms.receivedAt')}:{' '}
                    <span className="text-neutral-light">
                      {formatDate(code.receivedAt, 'PP h:mm a', currentLanguage)}
                    </span>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Codes Message */}
      {!connectionOnly && !isWaitingForCode && !hasCodes && (
        <div className="text-center py-8">
          <p className="text-neutral-medium font-body">
            {t('temporaryNumbers.sms.noCodes')}
          </p>
        </div>
      )}
    </div>
  );
}

